import JsBarcode from 'jsbarcode';
import { useEffect, useRef, useState, type ReactNode } from 'react';

interface BarcodeProps {
  value: string;
  height?: number;
  /** Called after the bars are drawn. */
  onReady?: () => void;
}

/** A Code 128 barcode as inline SVG, so it prints crisp at any size. */
export function Barcode({ value, height = 40, onReady }: BarcodeProps): ReactNode {
  const ref = useRef<SVGSVGElement>(null);
  const [failed, setFailed] = useState(false);
  const readyRef = useRef(onReady);
  readyRef.current = onReady;

  useEffect(() => {
    const svg = ref.current;
    if (svg === null) return;
    try {
      JsBarcode(svg, value, { format: 'CODE128', displayValue: true, margin: 0, height });
      svg.setAttribute('data-drawn', 'true');
      setFailed(false);
    } catch {
      // An empty or unencodable value must not unmount the app: print it as text.
      setFailed(true);
    }
    readyRef.current?.();
  }, [value, height]);

  return (
    <>
      <svg ref={ref} role="img" aria-label={value} className="print-barcode" display={failed ? 'none' : undefined} />
      {failed ? <span className="print-barcode-text">{value}</span> : null}
    </>
  );
}
